import {ButtonV2 as Button, Tooltip} from '@jobseeker/lotus/elements';
import {
  Box,
  Typography as Typo,
  Card as MuiCard,
  styled,
  Link,
  TypographyProps,
  Skeleton,
} from '@mui/material';
import {useState} from 'react';
import CustomDialog from '@jobseeker/lotus/components/DialogWithBottomSheet';
import {useSelector} from 'react-redux';
import {AppState} from '@jobseeker/common-core/types';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';
import {EmailVerification} from './EmailVerification';
import {info, SurveyCheckGreen} from '@jobseeker/lotus/assets/icons';
import {Image} from '@jobseeker/lotus/components/Image';
import {OtpComponent} from '../SecuritySetting/OtpComponent';
import {getOldLink} from '@jobseeker/lotus/utils';
import userService from '@jobseeker/common-core/services/user.service';
import {enqueueSnackbar} from 'notistack';

const Card = styled(MuiCard)(({theme}) => ({
  borderRadius: theme.spacing(2),
}));

const Typography = styled(Typo)<TypographyProps>(({}) => ({
  display: 'flex',
  alignItems: 'center',
}));

const VerifyContactSettingWidget = (props: {loading: boolean}) => {
  const {profile} = useSelector((state: AppState) => state.user);
  const {loading} = props;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogContent, setDialogContent] = useState<React.ReactNode>(null);
  const [dialogTitle, setDialogTitle] = useState('');
  const [dialogSubTitle, setDialogSubTitle] = useState('');
  const [showEmailBanner, setShowEmailBanner] = useState(false);
  const [errorText, setErrorText] = useState<string>('');
  const user = useSelector((state: AppState) => state.user.profile);

  const isLarge = useIsLargeDevice();
  const openDialog = (
    title: string,
    content: React.ReactNode,
    subTitle?: string
  ) => {
    setDialogTitle(title);

    if (subTitle) setDialogSubTitle(subTitle);
    setDialogContent(content);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
  };

  const handleSendOtp = async (): Promise<0 | 1> => {
    try {
      const response = await userService.sendOtp({
        phone: profile?.phone,
        countryCode: profile?.countryCode,
        isForVerification: true,
      });
      if (response?.error) {
        enqueueSnackbar(response.error, {variant: 'error'});
        return 0;
      }
      return 1;
    } catch (error) {
      enqueueSnackbar('Failed to send OTP', {variant: 'error'});
      return 0;
    }
  };

  const handleVerifyClick = async () => {
    if (user?.countryCode !== 91) {
      setDialogTitle(
        "It looks like you're requesting an OTP from outside our supported regions. Please reach out to us at info@iimjobs.com to verify your number."
      );
      setDialogContent(
        <>
          <Box display="flex" gap={1.5} mt={2} mb={2}>
            <Button
              onClick={() => {
                setDialogOpen(false);
              }}
              color="secondary"
              variant="contained"
            >
              Okay
            </Button>
          </Box>
        </>
      );

      setDialogOpen(true);
      return;
    }

    const result = await handleSendOtp();

    if (result === 1) {
      openDialog(
        `We have sent you an OTP on your number ${user?.phone}`,
        <OtpComponent
          setDialogOpen={setDialogOpen}
          isForChangeNumber={false}
          isForVerification={true}
        />,
        'Please check your Phone'
      );
    }
  };

  const handleResendEmail = async () => {
    try {
      const resInfo = await userService.sendVerifyEmail();

      if (resInfo?.error?.code == 429) {
        setErrorText(resInfo?.error?.message);
        return;
      }
      if (resInfo?.error?.message) {
        enqueueSnackbar('Failed to resend verification email', {
          variant: 'error',
        });
        return;
      }
      enqueueSnackbar('Verification email resent', {variant: 'success'});

      setShowEmailBanner(true);
    } catch (error) {
      enqueueSnackbar('An error occurred while resending the email', {
        variant: 'error',
      });
    }
  };

  return (
    <>
      <Card
        sx={{
          boxShadow: 2,
          borderRadius: 4,
          marginLeft: {md: 2},
        }}
      >
        <Box>
          {isLarge && (
            <Typography
              display="flex"
              fontSize={{xxs: 16, md: 20}}
              fontWeight={{xxs: 600, md: 500}}
              m={2}
              mt={3}
              sx={{
                wordWrap: 'break-word',
                textTransform: 'capitalize',
              }}
            >
              Verify Contact Details
              <Tooltip
                disableHoverListener={!isLarge}
                disableFocusListener={!isLarge}
                disableTouchListener={!isLarge}
                placement="bottom-start"
                sx={{
                  '& .MuiTooltip-tooltip': {
                    minWidth: '346px',
                    padding: '12px',
                  },
                }}
                title={
                  <>
                    <Typography
                      display={'flex'}
                      component={'p'}
                      mb={{xxs: 0.5, md: 0}}
                      variant="subtitle3"
                    >
                      Candidates who verify their contact details are more
                      <br />
                      likely to get contacted by the recruiters.
                    </Typography>
                  </>
                }
                arrow
              >
                <Image
                  src={info}
                  alt={'info'}
                  width={14}
                  height={14}
                  style={{marginLeft: 4}}
                />
              </Tooltip>
            </Typography>
          )}
          {loading ? (
            <Box px={1} pb={4}>
              <Skeleton
                variant="rounded"
                width={'50%'}
                height={21}
                sx={{
                  mt: 3,
                  mb: 1,
                }}
              />
              {[
                ...new Array(5).fill('').map(() => (
                  <>
                    <Skeleton
                      variant="rounded"
                      width={'100%'}
                      height={14}
                      sx={{
                        my: 0.5,
                      }}
                    />
                  </>
                )),
              ]}
              <Skeleton
                variant="rounded"
                width={'50%'}
                height={21}
                sx={{
                  mt: 3,
                  mb: 1,
                }}
              />
              <Skeleton
                variant="rounded"
                width={'50%'}
                height={21}
                sx={{
                  mt: 3,
                  mb: 1,
                }}
              />
            </Box>
          ) : (
            <Box mt={isLarge ? 0 : 3}>
              <Box
                color="gray.dark"
                onClick={() => {}}
                data-testid="notification_list_item"
                sx={{
                  position: 'relative',
                  transition: 'all ease 0.2s',
                  cursor: 'pointer',
                  // borderBottom: 'solid 1px',
                  // borderColor: 'border.lightGray',
                }}
              >
                <Box
                  sx={{
                    borderBottom: 'solid 1px',
                    borderColor: 'border.lightGray',
                    pt: {xxs: 1, md: 2},
                    pb: {xxs: 2, md: 2},
                    pl: {xxs: 1, md: 2},
                    pr: {xxs: 1, md: 3},
                  }}
                >
                  <Box>
                    <Box
                      display={'flex'}
                      mt={-1}
                      sx={{
                        width: {xxs: '100%', md: 680},
                      }}
                    >
                      <Box display={'flex'} flexDirection={'column'}>
                        {!!user.verifiedEmail ? (
                          <Box display={'flex'} alignItems={'center'} gap={1}>
                            <Image
                              src={SurveyCheckGreen}
                              width={24}
                              height={24}
                              alt="successIcon"
                            />
                            <Typography
                              color={'#333333'}
                              fontWeight={500}
                              fontSize={{xxs: 14, md: 16}}
                              lineHeight={'18px'}
                              sx={{
                                lineClamp: '3',
                                '-webkit-line-clamp': '2',
                                '-webkit-box-orient': 'vertical',
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                wordWrap: 'break-word',
                                display: 'block',
                                alignItems: 'center',
                              }}
                            >
                              Your email address has been verified!
                            </Typography>
                          </Box>
                        ) : (
                          <Typography
                            color={'#333333'}
                            fontWeight={500}
                            fontSize={14}
                            lineHeight={'20px'}
                            sx={{
                              lineClamp: '3',
                              '-webkit-line-clamp': '2',
                              '-webkit-box-orient': 'vertical',
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                              wordWrap: 'break-word',
                              display: 'block',
                              alignItems: 'center',
                            }}
                          >
                            Verify your Email Address
                          </Typography>
                        )}

                        {!!!user.verifiedEmail && !showEmailBanner && (
                          <>
                            <Button
                              variant="text"
                              onClick={handleResendEmail}
                              sx={{
                                padding: '0px',
                                fontSize: '14px',
                                lineHeight: '18px',
                                fontWeight: '500',
                                mt: 0.5,
                                color: 'primary.main',
                                cursor: 'pointer',
                                display: 'flex',
                                justifyContent: 'flex-start',
                                '&:hover': {
                                  backgroundColor: 'transparent',
                                },
                              }}
                            >
                              Verify
                            </Button>
                            {errorText.length > 0 && (
                              <Typography
                                color={'text.primary'}
                                variant="body3"
                                lineHeight={'16px'}
                                mt={1}
                              >
                                ({errorText})
                              </Typography>
                            )}
                          </>
                        )}
                      </Box>
                    </Box>
                    <CustomDialog
                      open={dialogOpen}
                      title={dialogTitle}
                      subTitle={dialogSubTitle}
                      Content={dialogContent}
                      onClose={closeDialog}
                    />
                    {!!!user.verifiedEmail && showEmailBanner && (
                      <EmailVerification
                        setShowEmailBanner={setShowEmailBanner}
                        setErrorText={setErrorText}
                        setDialogOpen={setDialogOpen}
                        setTitle={setDialogTitle}
                        setSubTitle={setDialogSubTitle}
                        setDialogContent={setDialogContent}
                      />
                    )}
                  </Box>
                </Box>
                <Box
                  sx={{
                    py: {xxs: 1, md: 2},
                    px: {xxs: 1, md: 2},
                  }}
                >
                  <Box>
                    <Box
                      display={'flex'}
                      sx={{
                        width: {xxs: '100%', md: 680},
                      }}
                    >
                      <Box display={'flex'} flexDirection={'column'}>
                        {!!user.verifiedNumber ? (
                          <Box
                            display={'flex'}
                            alignItems={'center'}
                            gap={1}
                            mb={{
                              xxs: 2,
                              md: !!user.verifiedEmail
                                ? 21.25
                                : showEmailBanner
                                ? 7.875
                                : errorText.length > 0
                                ? 16.375
                                : 19.25,
                            }}
                          >
                            <Image
                              src={SurveyCheckGreen}
                              width={24}
                              height={24}
                              alt="successIcon"
                            />
                            <Typography
                              color={'#333333'}
                              fontWeight={500}
                              fontSize={{xxs: 14, md: 16}}
                              lineHeight={'18px'}
                              sx={{
                                lineClamp: '3',
                                '-webkit-line-clamp': '2',
                                '-webkit-box-orient': 'vertical',
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                wordWrap: 'break-word',
                                display: 'block',
                                alignItems: 'center',
                              }}
                            >
                              Your phone number has been verified!
                            </Typography>
                          </Box>
                        ) : (
                          <Typography
                            color={'#333333'}
                            fontWeight={500}
                            fontSize={14}
                            lineHeight={'18px'}
                            sx={{
                              lineClamp: '3',
                              '-webkit-line-clamp': '2',
                              '-webkit-box-orient': 'vertical',
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                              wordWrap: 'break-word',
                              display: 'block',
                              alignItems: 'center',
                            }}
                          >
                            Verify your Phone Number
                          </Typography>
                        )}
                        {user?.phone ? (
                          !!!user.verifiedNumber && (
                            <Button
                              variant="text"
                              onClick={handleVerifyClick}
                              sx={{
                                padding: '0px',
                                lineHeight: '18px',
                                fontSize: '14px',
                                fontWeight: '500',
                                mt: 0.5,
                                mb: {
                                  xxs: 2,
                                  md: !!user.verifiedEmail
                                    ? 19.25
                                    : showEmailBanner
                                    ? 5.75
                                    : errorText.length > 0
                                    ? 14.25
                                    : 17.25,
                                },
                                color: 'var(--brand-color, #49966E)',
                                display: 'flex',
                                justifyContent: 'flex-start',
                                '&:hover': {
                                  backgroundColor: 'transparent',
                                },
                              }}
                            >
                              Verify
                            </Button>
                          )
                        ) : (
                          <Typography
                            color="text.primary"
                            variant="body2"
                            lineHeight={'18px'}
                            mt={0.5}
                            mb={{
                              xxs: 2,
                              md: user?.verifiedEmail
                                ? 19.5
                                : showEmailBanner
                                ? 5.625
                                : errorText.length > 0
                                ? 14.25
                                : 17.25,
                            }}
                            sx={{
                              whiteSpace: {xxs: 'normal', md: 'nowrap'},
                              display: 'inline',
                            }}
                          >
                            We couldn’t find any number linked to your account.
                            Please
                            <Link
                              href={getOldLink(
                                `/registration/addPersonalDetails`
                              )}
                              sx={{
                                color: 'var(--brand-color, #49966E)',
                                textDecoration: 'none',
                                padding: 0,
                                fontWeight: 500,
                                margin: '0 4px',
                                fontSize: {xxs: 12, md: 14},
                                display: 'inline-block',
                                verticalAlign: 'baseline',
                                '&:hover': {
                                  color: 'var(--brand-color, #49966E)',
                                  textDecoration: 'none',
                                },
                                '&:focus, &:active': {
                                  color: 'var(--brand-color, #49966E)',
                                  textDecoration: 'none',
                                },
                              }}
                              onClick={(
                                e: React.MouseEvent<HTMLAnchorElement>
                              ) => {
                                e.stopPropagation();
                              }}
                            >
                              Click here
                            </Link>
                            to add your Phone number
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Card>
    </>
  );
};

export default VerifyContactSettingWidget;
